// Tanstack Start server function that exposes the `toggleTalentStatus`
// use case to client code. Same posture as `listLiveInClub.fn.ts`: resolve
// the authenticated scope, open a `tenancy.withTenant(clubId, …)` scope,
// run the composed use case.
//
// Returns null when there is no active session — the route guard
// (`src/routes/_authenticated.tsx`) already redirects unauthenticated
// requests, so reaching this handler without a session is the defensive
// path (e.g. session expired between the page render and a mutation).

import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import { statusValueSchema } from '../domain/status';
import type { TalentStatusRow } from '../domain/types';

const inputSchema = z.object({
	talentId: z.string().uuid(),
	status: statusValueSchema,
	// Optional restore value used by the undo path so the server resets
	// lastInClubAt to the row's pre-toggle value instead of preserving the
	// just-bumped one. ISO-8601 datetime; nullable when the prior value was
	// null. Absent on fresh toggles (the default invariant applies).
	restore_last_in_club_at: z.string().datetime().nullable().optional(),
});

export const toggleTalentStatusServerFn = createServerFn({ method: 'POST' })
	.inputValidator((data: unknown) => inputSchema.parse(data))
	.handler(async ({ data }): Promise<TalentStatusRow | null> => {
		const { getServerAuthService } = await import(
			'../../authentication/composition.server'
		);
		const { getServerTenancyService } = await import(
			'../../../_shared/tenancy/composition.server'
		);
		const { getServerToggleTalentStatus } = await import(
			'../composition.server'
		);

		const auth = getServerAuthService();
		const scope = await auth.resolveTenantScope(getRequest());
		if (!scope) return null;

		const tenancy = getServerTenancyService();
		const toggle = await getServerToggleTalentStatus();
		return tenancy.withTenant(scope.clubId, () =>
			toggle(data.talentId, data.status, data.restore_last_in_club_at),
		);
	});
